import {
  useState,
  useEffect,
  useMemo,
} from "react";
import { useLocation,
  useNavigate } from "react-router-dom";
import { Check,
  Star,
  Crown,
  Award,
  CheckCircle2,
  X,
  ChevronsUpDown } from "lucide-react";
import {
  Button,
  Label,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  extractRootIndustryOptions,
  formatMembershipPlanPrice,
} from "@b2b/ui-components";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@b2b/ui-components";
import { membershipApi, MembershipPlan, profileService, adminService , authService} from "@b2b/services";
import { useAuth } from "../../auth/Authprovider";
import { useChatUnreadHeader } from "../../chat/ChatUnreadHeaderProvider";
import { BizcoverIndiaFigmaHeader } from "../../components/marketing/BizcoverIndiaFigmaHeader";
import { BizcoverIndiaFigmaFooter } from "../../components/marketing/BizcoverIndiaFigmaFooter";

/** Canonical public pricing order: Platinum → Gold → Silver → Free. */
const PRICING_PLAN_ORDER = ["platinum", "gold", "silver", "free"] as const;

function getPricingPlanSortIndex(planName: string): number {
  const n = planName.trim().toLowerCase();
  const idx = PRICING_PLAN_ORDER.findIndex(
    (tier) => n === tier || n.includes(tier),
  );
  return idx === -1 ? PRICING_PLAN_ORDER.length : idx;
}

function sortPlansForPublicPricing(plans: MembershipPlan[]): MembershipPlan[] {
  return [...plans].sort((a, b) => {
    const orderDiff =
      getPricingPlanSortIndex(a.name) - getPricingPlanSortIndex(b.name);
    if (orderDiff !== 0) return orderDiff;
    return a.name.localeCompare(b.name);
  });
}

function getPlanTierKey(planName: string): (typeof PRICING_PLAN_ORDER)[number] | "other" {
  const n = planName.trim().toLowerCase();
  const tier = PRICING_PLAN_ORDER.find((t) => n === t || n.includes(t));
  return tier ?? "other";
}

function getPublicPricingCardClasses(
  planName: string,
  isPopular: boolean,
): string {
  const tier = getPlanTierKey(planName);
  const base =
    "bg-white rounded-2xl border overflow-visible transition-shadow duration-300 ease-out";
  switch (tier) {
    case "platinum":
      return `${base} border-[#c7d2e0] shadow-[0px_12px_40px_rgba(30,58,95,0.14)] hover:shadow-[0px_18px_48px_rgba(30,58,95,0.2)]`;
    case "gold":
      return `${base} border-[#FFE0C9] shadow-[0px_10px_36px_rgba(255,106,0,0.14)] hover:shadow-[0px_16px_44px_rgba(255,106,0,0.2)] ring-2 ring-[#FF6A00]/30`;
    case "silver":
      return `${base} border-gray-200 shadow-[0px_8px_28px_rgba(15,52,96,0.1)] hover:shadow-[0px_14px_36px_rgba(15,52,96,0.14)]`;
    case "free":
      return `${base} border-gray-100 shadow-[0px_4px_18px_rgba(15,52,96,0.06)] hover:shadow-[0px_10px_28px_rgba(15,52,96,0.1)]`;
    default:
      return isPopular
        ? `${base} border-orange-200 shadow-[0px_10px_32px_rgba(249,115,22,0.12)] hover:shadow-[0px_14px_40px_rgba(249,115,22,0.16)] ring-1 ring-orange-500/20`
        : `${base} border-gray-200 shadow-[0px_8px_28px_rgba(15,52,96,0.08)] hover:shadow-[0px_12px_36px_rgba(15,52,96,0.12)]`;
  }
}

function getPublicPricingIconClasses(planName: string): string {
  switch (getPlanTierKey(planName)) {
    case "platinum":
      return "bg-[#1e3a5f] text-white";
    case "gold":
      return "bg-[#FF6A00] text-white";
    case "silver":
      return "bg-[#64748b] text-white";
    case "free":
      return "bg-gray-100 text-gray-600";
    default:
      return "bg-muted text-muted-foreground";
  }
}

function normalizePlanFeatures(features: unknown): string[] {
  if (Array.isArray(features)) {
    return features
      .map((feature) => (typeof feature === "string" ? feature.trim() : String(feature ?? "").trim()))
      .filter(Boolean);
  }
  if (typeof features === "string") {
    try {
      const parsed = JSON.parse(features);
      return normalizePlanFeatures(parsed);
    } catch {
      return features.trim() ? [features.trim()] : [];
    }
  }
  return [];
}

const MembershipPlans = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, refetchUserStatus } = useAuth();
  const chatUnread = useChatUnreadHeader();
  const [plans, setPlans] = useState<MembershipPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [currentSubscriptionId, setCurrentSubscriptionId] = useState<string | null>(null);
  const [subscribingPlanId, setSubscribingPlanId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Seller: industry selection prompt state (plan-specific max limit)
  const [companyIndustryTypes, setCompanyIndustryTypes] = useState<string[]>([]);
  const [industrySelectionPlanId, setIndustrySelectionPlanId] = useState<string | null>(null);
  const [industryDraft, setIndustryDraft] = useState<string[]>([]);
  const [industryPromptError, setIndustryPromptError] = useState<string | null>(null);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [level1Categories, setLevel1Categories] = useState<Array<{ id: string; name: string }>>([]);
  const [industryPopoverOpen, setIndustryPopoverOpen] = useState(false);
  const [industriesLoading, setIndustriesLoading] = useState(false);

  const isAdminOrSellerRoute = location.pathname.includes("/admin/membership_plans") ||
                                location.pathname.includes("/seller/membership_plans");
  const showBuyerHeader = !isAdminOrSellerRoute;
  const isSellerRoute = location.pathname.includes("/seller/membership_plans");
  const isPricingRoute = location.pathname === "/pricing";
  const isMembershipPlansRoute = location.pathname.includes("membership_plans");
  /** Same card layout, styling, and Platinum → Gold → Silver → Free order as /pricing. */
  const usesPricingLayout =
    isPricingRoute ||
    location.pathname === "/membership-plans" ||
    isMembershipPlansRoute;
  const pricingPageBgClass = usesPricingLayout ? "bg-[#fafafa]" : "";

  const getIndustrySelectionLimit = (plan: MembershipPlan | undefined | null): number => {
    if (!plan) return 1;
    const features = Array.isArray(plan.features) ? plan.features : [];
    for (const f of features) {
      if (typeof f !== "string") continue;
      const match = f.match(/(\d+)\s*Industry Selections/i);
      if (match) return Number(match[1]);
    }

    // Fallback based on seeded plan names
    switch (plan.name) {
      case "Free":
        return 1;
      case "Silver":
        return 1;
      case "Gold":
        return 2;
      case "Platinum":
        return 5;
      default:
        return 1;
    }
  };
  const [hasToken, setHasToken] = useState<boolean>(false);
  const [currentUserRole, setCurrentUserRole] = useState("seller");
  useEffect(() => {
    const checkToken = () => {
      setHasToken(!!localStorage.getItem("bt_b2b_access_token"));
    };
    checkToken();
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "bt_b2b_access_token") checkToken();
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);
  
  useEffect(() => {
    if (hasToken) {
      const fetchUserData = async () => {
        try {
          const meResponse = await authService.getMe();
          if (meResponse.status === "active" && meResponse.user) {
            const user = meResponse.user;
            const userName =
              user.firstName && user.lastName
                ? `${user.firstName} ${user.lastName}`
                : user.firstName || user.email || "";
            const currentUserRole = user.role?.toLowerCase();
            setCurrentUserRole(currentUserRole);
          }
        } catch (error) {
          console.error("Error fetching user data in Header:", error);
        }
      };
      fetchUserData();
    }
  }, [hasToken]);
  useEffect(() => {
    const loadPlans = async () => {
      try {
        setLoading(true);
        const plansPromise = membershipApi.getActivePlans();
        const subPromise = isMembershipPlansRoute
          ? membershipApi.getCompanySubscription().catch(() => null)
          : Promise.resolve(null);
        const [plansData, subscription] = await Promise.all([plansPromise, subPromise]);
        // Ensure plansData is an array and normalize features
        const normalizedPlans = Array.isArray(plansData)
          ? plansData.map((plan) => ({
              ...plan,
              features: normalizePlanFeatures(plan.features),
            }))
          : [];
        setPlans(normalizedPlans);
        if (subscription?.planId) {
          setCurrentPlanId(subscription.planId);
          setCurrentSubscriptionId(subscription.id);
        } else {
          setCurrentPlanId(null);
          setCurrentSubscriptionId(null);
        }
      } catch (error) {
        console.error("Failed to load plans:", error);
        // Set empty array on error to prevent filter errors
        setPlans([]);
      } finally {
        setLoading(false);
      }
    };

    loadPlans();
  }, [isMembershipPlansRoute]);

  useEffect(() => {
    let cancelled = false;
    const loadCompanyIndustryTypes = async () => {
      try {
        if (!isSellerRoute) return;
        setIndustriesLoading(true);

        const response = await profileService.getProfile();
        const raw = response as any;
        const data = raw?.success === true ? raw.data : raw?.data ?? raw;

        const industryTypes = Array.isArray(data?.company?.industryTypes)
          ? (data.company.industryTypes as string[])
          : [];

        if (cancelled) return;
        setCompanyIndustryTypes(industryTypes.filter(Boolean));
      } catch (e) {
        console.error("Failed to load industries for membership selection:", e);
        if (!cancelled) setCompanyIndustryTypes([]);
      } finally {
        if (!cancelled) setIndustriesLoading(false);
      }
    };

    loadCompanyIndustryTypes();
    return () => {
      cancelled = true;
    };
  }, [isSellerRoute]);

  useEffect(() => {
    let cancelled = false;
    const loadLevel1Categories = async () => {
      if (!isSellerRoute || !industrySelectionPlanId) return;
      if (level1Categories.length > 0) return;
      try {
        setCategoriesLoading(true);
        const data = await adminService.getCategories(true, false, 1);
        if (cancelled) return;

        setLevel1Categories(
          extractRootIndustryOptions(Array.isArray(data) ? data : []),
        );
      } catch (e) {
        console.error("Failed to load categories for membership industry selection:", e);
        if (!cancelled) setLevel1Categories([]);
      } finally {
        if (!cancelled) setCategoriesLoading(false);
      }
    };

    loadLevel1Categories();
    return () => {
      cancelled = true;
    };
  }, [adminService, isSellerRoute, industrySelectionPlanId, level1Categories.length]);

  const handleSubscribe = async (
    planId: string,
    subscriptionIdToCancel?: string | null,
    industries?: string[]
  ) => {
    const wasCurrentPlanId = currentPlanId;
    setSubscribingPlanId(planId);
    setMessage(null);
    try {
      if (subscriptionIdToCancel) {
        await membershipApi.cancelSubscription(subscriptionIdToCancel);
        setCurrentSubscriptionId(null);
      }

      if (isSellerRoute && industries) {
        const plan = plans.find((p) => p.id === planId);
        const limit = getIndustrySelectionLimit(plan);
        if (industries.length > limit) {
          throw new Error(`Selected industries exceed max ${limit} for this plan.`);
        }
      }

      const newSubscription = await membershipApi.subscribeToPlan({ planId, industries });
      setCurrentPlanId(planId);
      setCurrentSubscriptionId(newSubscription?.id ?? null);
      refetchUserStatus();

      if (isSellerRoute) {
        setIndustrySelectionPlanId(null);
        setIndustryPromptError(null);
        setIndustryDraft([]);
        setCompanyIndustryTypes(industries ?? companyIndustryTypes);
      }

      setMessage({
        type: "success",
        text: wasCurrentPlanId ? "Plan switched successfully." : "Plan selected successfully. Step 3 (Storefront) is now unlocked.",
      });
      if (isSellerRoute && !wasCurrentPlanId) {
        setTimeout(() => navigate("/seller/store_front"), 1500);
      }
    } catch (error: unknown) {
      const errMsg = error instanceof Error ? error.message : "Failed to select plan.";
      setMessage({ type: "error", text: errMsg });
    } finally {
      setSubscribingPlanId(null);
    }
  };

  const selectedIndustryPlan =
    plans.find((p) => p.id === industrySelectionPlanId) ?? null;
  const industrySelectionLimit = getIndustrySelectionLimit(selectedIndustryPlan);
  const exceedsIndustryLimit = industryDraft.length > industrySelectionLimit;
  const atIndustryLimit = industryDraft.length >= industrySelectionLimit;
  const industryIdToName = (id: string) =>
    level1Categories.find((c) => c.id === id)?.name ?? id;

  /** Pricing layout: Platinum → Gold → Silver → Free. */
  const plansForGrid = useMemo(() => {
    const base = Array.isArray(plans)
      ? plans.filter((plan) => plan && typeof plan.name === "string")
      : [];
    if (!usesPricingLayout) return base;
    return sortPlansForPublicPricing(base);
  }, [plans, usesPricingLayout]);

  if (loading) {
    return (
      <div className={`min-h-screen flex flex-col ${pricingPageBgClass}`}>
        {showBuyerHeader && (
          <BizcoverIndiaFigmaHeader />
        )}
        <section className={`py-12 flex-1 ${usesPricingLayout ? "" : "bg-background"}`}>
          <div className="container mx-auto px-4">
            <div className="text-center">
              <p>Loading membership plans...</p>
            </div>
          </div>
        </section>
        {showBuyerHeader && (
          <BizcoverIndiaFigmaFooter />
        )}
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col ${pricingPageBgClass}`}>
      {showBuyerHeader && (
        <BizcoverIndiaFigmaHeader />
      )}
      <section
        className={`flex-1 ${
          usesPricingLayout
            ? "py-10 sm:py-14"
            : "py-6 sm:py-8 bg-background"
        }`}
      >
        {usesPricingLayout && (
          <style>{`
            @keyframes pricing-card-float {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-6px); }
            }
            .pricing-card-float {
              animation: pricing-card-float 3s ease-in-out infinite;
              will-change: transform;
            }
            @media (prefers-reduced-motion: reduce) {
              .pricing-card-float { animation: none; }
            }
          `}</style>
        )}
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`text-center mb-8 sm:mb-10 ${
              usesPricingLayout ? "sm:mb-14" : ""
            }`}
          >
            <h2
              className={`mb-3 ${
                usesPricingLayout
                  ? "text-4xl sm:text-5xl font-extrabold tracking-tight text-[#1A1A1A]"
                  : "text-2xl sm:text-3xl font-bold text-foreground"
              }`}
            >
              {isSellerRoute
                ? "Choose Your Membership Plan"
                : usesPricingLayout
                  ? "Pricing Plans"
                  : "Membership Plans"}
            </h2>
            <p
              className={`mx-auto ${
                usesPricingLayout
                  ? "max-w-2xl text-base sm:text-lg text-[#666666] leading-relaxed"
                  : "max-w-xl text-sm sm:text-base text-muted-foreground"
              }`}
            >
              Unlock powerful features to grow your business and connect with global buyers and suppliers
            </p>
          </div>

          {message && (
            <div
              className={`mb-6 rounded-lg border px-4 py-3 text-sm ${
                message.type === "success"
                  ? "border-green-200 bg-green-50 text-green-800"
                  : "border-red-200 bg-red-50 text-red-800"
              }`}
            >
              {message.text}
            </div>
          )}

          <div
            className={`grid w-full ${
              usesPricingLayout
                ? "grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 sm:gap-6 xl:gap-8"
                : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
            }`}
          >
            {plansForGrid.length > 0 ? (
              plansForGrid.map((plan, index) => {
                  const getIcon = (planName: string) => {
                    const n = planName.toLowerCase();
                    if (n.includes("platinum")) return Crown;
                    if (n.includes("gold")) return Star;
                    if (n.includes("silver")) return Award;
                    if (n.includes("free")) return Check;
                    if (n.includes("basic")) return Award;
                    if (n.includes("standard") || n.includes("professional"))
                      return Star;
                    if (n.includes("premium") || n.includes("enterprise"))
                      return Crown;
                    return Star;
                  };

                  const Icon = getIcon(plan.name);
                  const tierKey = getPlanTierKey(plan.name);
                  const priceDisplay = formatMembershipPlanPrice(
                    plan.price,
                    plan.currency,
                    plan.billingCycle,
                  );
                  const isSubscribing = subscribingPlanId === plan.id;
                  const isCurrentPlan = plan.id === currentPlanId;
                  const isIndustryTargetPlan = industrySelectionPlanId === plan.id;

                  const publicCardClasses = usesPricingLayout
                    ? getPublicPricingCardClasses(plan.name, plan.isPopular)
                    : "";
                  const sellerAdminCardClasses = !usesPricingLayout
                    ? isCurrentPlan
                      ? "border-2 border-emerald-500 bg-emerald-50/50 ring-2 ring-emerald-500/20 shadow-md"
                      : plan.isPopular
                        ? "border-orange-500 ring-2 ring-orange-500/20 shadow-md hover:shadow-lg"
                        : "border-border hover:border-orange-300 shadow-sm hover:shadow-md"
                    : "";

                  const showTopRibbon = isCurrentPlan || plan.isPopular;
                  const isPublicPopularPill =
                    usesPricingLayout && !isCurrentPlan && plan.isPopular;

                  return (
                    <Card
                      key={plan.id}
                      style={
                        usesPricingLayout
                          ? { animationDelay: `${index * 0.3}s` }
                          : undefined
                      }
                      className={`relative flex min-w-0 flex-col overflow-hidden ${
                        usesPricingLayout
                          ? `${publicCardClasses} pricing-card-float`
                          : `transition-all duration-200 ${sellerAdminCardClasses}`
                      }`}
                    >
                      {usesPricingLayout ? (
                        <>
                          {isCurrentPlan && (
                            <span className="absolute left-1/2 top-0 z-10 flex -translate-x-1/2 -translate-y-1/2 items-center gap-1 whitespace-nowrap rounded-full bg-emerald-600 px-4 py-1 text-[11px] font-semibold uppercase tracking-wider text-white shadow-md">
                              <CheckCircle2 className="h-3 w-3 shrink-0" />
                              Current Plan
                            </span>
                          )}
                          {isPublicPopularPill && (
                            <span className="absolute left-1/2 top-0 z-10 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full bg-[#FF6A00] px-4 py-1 text-[11px] font-semibold uppercase tracking-wider text-white shadow-md">
                              Most Popular
                            </span>
                          )}
                        </>
                      ) : (
                        <>
                          {isCurrentPlan && (
                            <div className="flex w-full items-center justify-center gap-1.5 rounded-t-2xl bg-emerald-600 px-3 py-2 text-center text-xs font-semibold text-white">
                              <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                              <span>Current Plan</span>
                            </div>
                          )}
                          {!isCurrentPlan && plan.isPopular && (
                            <div className="w-full rounded-t-2xl bg-[#F97316] px-3 py-2 text-center text-xs font-semibold tracking-wide text-white">
                              Most Popular
                            </div>
                          )}
                        </>
                      )}

                      <CardHeader
                        className={`min-w-0 w-full text-center pb-2 ${
                          usesPricingLayout
                            ? "pt-10 px-4 sm:px-5"
                            : showTopRibbon ? "pt-5 px-4" : "pt-6 px-4"
                        }`}
                      >
                        <div className="flex justify-center mb-3">
                          <div
                            className={`rounded-full flex items-center justify-center shrink-0 ${
                              usesPricingLayout ? "w-14 h-14" : "w-12 h-12"
                            } ${
                              usesPricingLayout
                                ? isCurrentPlan
                                  ? "bg-emerald-600 text-white"
                                  : getPublicPricingIconClasses(plan.name)
                                : isCurrentPlan
                                  ? "bg-emerald-600 text-white"
                                  : plan.isPopular
                                    ? "bg-orange-500 text-white"
                                    : "bg-muted text-muted-foreground"
                            }`}
                          >
                            <Icon
                              className={`${
                                usesPricingLayout ? "w-7 h-7" : "w-6 h-6"
                              } ${
                                usesPricingLayout ||
                                isCurrentPlan ||
                                plan.isPopular
                                  ? ""
                                  : "text-muted-foreground"
                              }`}
                            />
                          </div>
                        </div>
                        <CardTitle
                          className={`${
                            usesPricingLayout
                              ? "text-2xl font-bold tracking-tight text-[#1A1A1A]"
                              : "text-lg font-bold"
                          }`}
                        >
                          {plan.name}
                        </CardTitle>
                        <div className="mt-3 w-full min-w-0">
                          <div
                            className={`mx-auto flex max-w-full flex-col items-center justify-center gap-0.5 ${
                              usesPricingLayout
                                ? "text-2xl sm:text-3xl lg:text-[1.75rem] xl:text-4xl font-extrabold leading-tight tracking-tight"
                                : "text-xl sm:text-2xl font-bold leading-tight"
                            } ${
                              isCurrentPlan
                                ? "text-emerald-700"
                                : usesPricingLayout
                                  ? tierKey === "gold"
                                    ? "text-[#FF6A00]"
                                    : "text-[#1A1A1A]"
                                  : tierKey === "platinum"
                                    ? "text-[#1e3a5f]"
                                    : tierKey === "gold"
                                      ? "text-[#F97316]"
                                      : tierKey === "silver"
                                        ? "text-[#64748b]"
                                        : "text-orange-500"
                            }`}
                          >
                            <span className="tabular-nums whitespace-nowrap">
                              {priceDisplay.amount}
                              {priceDisplay.cycle ? (
                                <span className="text-[0.72em] font-bold">
                                  /{priceDisplay.cycle}
                                </span>
                              ) : null}
                            </span>
                          </div>
                          {priceDisplay.gstLine ? (
                            <p
                              className={`mt-1 font-semibold whitespace-nowrap ${
                                usesPricingLayout
                                  ? "text-sm text-[#666666]"
                                  : "text-xs text-muted-foreground"
                              }`}
                            >
                              {priceDisplay.gstLine}
                            </p>
                          ) : null}
                        </div>
                        {plan.description && (
                          <p
                            className={`line-clamp-2 ${
                              usesPricingLayout
                                ? "mt-3 text-sm text-[#666666] leading-relaxed"
                                : "mt-1 text-xs text-muted-foreground"
                            }`}
                          >
                            {plan.description}
                          </p>
                        )}
                      </CardHeader>

                      <CardContent
                        className={`flex flex-col flex-1 pt-0 ${
                          usesPricingLayout ? "space-y-5 px-6" : "space-y-4"
                        }`}
                      >
                        <div
                          className={`flex-1 min-h-0 ${
                            usesPricingLayout ? "space-y-3" : "space-y-2"
                          }`}
                        >
                          {Array.isArray(plan.features) && plan.features.length > 0 ? (
                            plan.features.map((feature, featureIndex) => (
                              <div
                                key={featureIndex}
                                className="flex gap-2 py-1 items-start"
                              >
                                <Check
                                  className={`flex-shrink-0 ${
                                    usesPricingLayout
                                      ? "w-4 h-4 mt-0.5 text-[#FF6A00]"
                                      : "w-3.5 h-3.5 mt-0.5 text-green-600"
                                  }`}
                                />
                                <span
                                  className={`break-words ${
                                    usesPricingLayout
                                      ? "text-sm leading-snug text-[#1A1A1A]"
                                      : "text-xs text-foreground leading-snug"
                                  }`}
                                >
                                  {feature}
                                </span>
                              </div>
                            ))
                          ) : (
                            <p className="text-xs text-muted-foreground py-2">
                              No features listed
                            </p>
                          )}
                        </div>

                        {!isPricingRoute && (
                          <Button
                            variant="outline"
                            size="default"
                            className={`w-full mt-auto ${
                              usesPricingLayout
                                ? plan.isPopular && !isCurrentPlan
                                  ? "bg-[#FF6A00] hover:bg-[#E85F00] text-white border-[#FF6A00] h-11 text-sm font-semibold"
                                  : "bg-white text-[#FF6A00] border-2 border-[#FF6A00] hover:bg-[#FFF5EE] h-11 text-sm font-semibold"
                                : ""
                            }`}
                            disabled={
                              isSubscribing ||
                              isCurrentPlan ||
                              (isSellerRoute && industriesLoading && industrySelectionPlanId === null)
                            }
                            onClick={() => {
                              if (isCurrentPlan) return;

                              if (isSellerRoute) {
                                setIndustryPromptError(null);
                                setIndustryPopoverOpen(false);

                                // If a prompt is already open and the new plan would exceed the limit,
                                // revert to the previously selected plan and require industry updates first.
                                if (industrySelectionPlanId) {
                                  const newLimit = getIndustrySelectionLimit(plan);
                                  if (industryDraft.length > newLimit) {
                                    setIndustryPromptError(
                                      `Reduce industries to max ${newLimit} to switch to the "${plan.name}" plan.`
                                    );
                                    return;
                                  }
                                  setIndustrySelectionPlanId(plan.id);
                                  return;
                                }

                                setIndustrySelectionPlanId(plan.id);
                                setIndustryDraft(companyIndustryTypes);
                                return;
                              }

                              handleSubscribe(
                                plan.id,
                                currentPlanId ? currentSubscriptionId : null
                              );
                            }}
                          >
                            {isSubscribing
                              ? "Updating..."
                              : isCurrentPlan
                                ? "Current Plan"
                                : isSellerRoute
                                  ? isIndustryTargetPlan
                                    ? "Update Industries"
                                    : currentPlanId
                                      ? "Switch to this plan"
                                      : plan.price === 0
                                        ? "Get Started"
                                        : "Select Plan"
                                  : currentPlanId
                                    ? "Switch to this plan"
                                    : plan.price === 0
                                      ? "Get Started"
                                      : "Select Plan"}
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  );
                })
            ) : (
              <div className="col-span-full text-center py-8 text-muted-foreground">
                No membership plans available.
              </div>
            )}
          </div>

          <Dialog
            open={isSellerRoute && !!industrySelectionPlanId && !!selectedIndustryPlan}
            onOpenChange={(open) => {
              // Treat outside/close as cancellation; do not reset if a subscribe request is in-flight.
              if (!open && subscribingPlanId == null) {
                setIndustrySelectionPlanId(null);
                setIndustryPromptError(null);
                setIndustryDraft(companyIndustryTypes);
                setIndustryPopoverOpen(false);
              }
            }}
          >
            {selectedIndustryPlan && (
              <DialogContent className="max-w-3xl bg-white">
                <DialogHeader>
                  <DialogTitle className="text-left">
                    Choose Industries for {selectedIndustryPlan.name}
                  </DialogTitle>
                </DialogHeader>

                <div className="mt-2 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Select up to {industrySelectionLimit} industr
                      {industrySelectionLimit === 1 ? "y" : "ies"}.
                    </p>
                    {industriesLoading && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Loading your saved industries...
                      </p>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">{industryDraft.length} selected</div>
                </div>

                {(industryPromptError || exceedsIndustryLimit) && (
                  <div
                    className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
                      "border-red-200 bg-red-50 text-red-800"
                    }`}
                  >
                    {industryPromptError
                      ? industryPromptError
                      : `You selected ${industryDraft.length} industries, but this plan allows up to ${industrySelectionLimit}. Please remove industries to continue.`}
                  </div>
                )}

                <div className="mt-4 space-y-1.5">
                  <Label className="text-sm font-medium text-gray-700">Industries</Label>
                  <p className="text-xs text-muted-foreground mb-1">
                    Maximum: {industrySelectionLimit}. No duplicates.
                  </p>

                  <Popover open={industryPopoverOpen} onOpenChange={setIndustryPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        disabled={categoriesLoading || industryDraft.length >= industrySelectionLimit}
                        className="h-10 max-w-[280px] justify-between border-gray-300 rounded bg-white font-normal text-gray-700 hover:bg-gray-50"
                      >
                        <span className="truncate">
                          {industryDraft.length >= industrySelectionLimit
                            ? `Maximum ${industrySelectionLimit} selected`
                            : "Select industry (search & scroll)"}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>

                    <PopoverContent
                      className="w-[var(--radix-popover-trigger-width)] min-w-[240px] max-w-[360px] bg-white p-0 border border-gray-200 shadow-md"
                      align="start"
                    >
                      <Command>
                        <CommandInput
                          placeholder="Search industry..."
                          className="h-9 border-0 border-b focus:ring-0"
                        />
                        <CommandList className="max-h-[300px] overflow-y-auto">
                          <CommandEmpty>No industry found.</CommandEmpty>
                          <CommandGroup>
                            {level1Categories
                              .filter((c) => !industryDraft.includes(c.id))
                              .map((c) => (
                                <CommandItem
                                  key={c.id}
                                  value={c.name}
                                  disabled={industryDraft.length >= industrySelectionLimit}
                                  className="cursor-pointer"
                                  onSelect={() => {
                                    setIndustryDraft((prev) => {
                                      if (prev.includes(c.id)) return prev;
                                      if (prev.length >= industrySelectionLimit) return prev;
                                      return [...prev, c.id];
                                    });
                                    setIndustryPopoverOpen(false);
                                  }}
                                >
                                  {c.name}
                                </CommandItem>
                              ))}
                            {level1Categories.filter((c) => !industryDraft.includes(c.id)).length === 0 && (
                              <div className="py-4 text-center text-sm text-muted-foreground">
                                {industryDraft.length >= industrySelectionLimit
                                  ? "Maximum reached"
                                  : "No more options"}
                              </div>
                            )}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>

                  {industryDraft.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-medium text-gray-600 mb-1.5">
                        Selected industries:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {industryDraft.map((id) => (
                          <span
                            key={id}
                            className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-800"
                          >
                            {industryIdToName(id)}
                            <button
                              type="button"
                              onClick={() =>
                                setIndustryDraft((prev) => prev.filter((x) => x !== id))
                              }
                              className="rounded-full p-0.5 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400"
                              aria-label={`Remove ${industryIdToName(id)}`}
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <DialogFooter className="mt-5 flex flex-col sm:flex-row gap-2 sm:gap-3 sm:justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={subscribingPlanId != null}
                    onClick={() => {
                      setIndustrySelectionPlanId(null);
                      setIndustryPromptError(null);
                      setIndustryDraft(companyIndustryTypes);
                      setIndustryPopoverOpen(false);
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="button"
                    variant="primary"

                    disabled={
                      categoriesLoading ||
                      exceedsIndustryLimit ||
                      industriesLoading ||
                      subscribingPlanId === industrySelectionPlanId
                    }
                    onClick={() => {
                      if (!industrySelectionPlanId) return;
                      handleSubscribe(
                        industrySelectionPlanId,
                        currentPlanId ? currentSubscriptionId : null,
                        industryDraft
                      );
                    }}
                  >
                    {subscribingPlanId === industrySelectionPlanId ? "Updating..." : "Confirm & Subscribe"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            )}
          </Dialog>
        </div>
      </section>
      {showBuyerHeader && (
        <BizcoverIndiaFigmaFooter />
      )}
    </div>
  );
};

export default MembershipPlans;
